import { FormikProps } from 'formik'
import { useStyles } from './AddEditKeywords.styles'
import {
  AutoCompleteWithMultiSelect,
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { useCallback, useState } from 'react'
import { KeywordCompetitorForm } from './AddEditKeywords'
import { get } from 'lodash'
import { useDispatch, useSelector } from '../../../store'
import {
  KeywordCompetitorMapping,
  CompetitorType,
  KeywordType,
} from '../../../modules/mpsSeo/reducer'
import { addEditKeywordSection } from '../../../modules/mpsSeo/action'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDeleteRight } from '@fortawesome/pro-regular-svg-icons'
import { SEO_CLOUD_MARKETPLACE, useSeoAccountId } from '../seoCloud'
import { getIsSaveDisabled } from './getIsSaveDisabled'

export const KeywordMapping = ({
  index,
  keywords,
  competitors,
  handleDelete,
  formikProps,
}: {
  index: number
  keywords: KeywordType[]
  competitors: CompetitorType[]
  handleDelete: (index: number) => void
  formikProps: FormikProps<KeywordCompetitorForm>
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = useSeoAccountId()
  const selectedCloud = SEO_CLOUD_MARKETPLACE

  const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false)

  const [saveLoading, setSaveLoading] = useState<boolean>(false)

  const { handleBlur, setFieldValue, touched, errors, values } = formikProps

  const keywordCompetitorList = useSelector<KeywordCompetitorMapping[]>(
    state => state.marketplaceSeo[selectedCloud].keywordCompetitorMapping
  )

  const existingKeywords = useSelector<KeywordType[]>(
    state => state?.marketplaceSeo[selectedCloud].keywords || []
  )
  const competitorsList = useSelector<CompetitorType[]>(
    state => state?.marketplaceSeo[selectedCloud].competitors || []
  )

  const [newKeywords, setNewKeywords] = useState<KeywordType[]>([])
  const [inputValue, setInputValue] = useState('')

  const newKeywordsOptionListOptions = newKeywords.map(item => ({
    label: item.name,
    value: item.name,
  }))

  const keywordsListOptions = existingKeywords?.map((item: KeywordType) => ({
    label: item?.name,
    value: item?.name,
  }))

  const competitorsListOptions = competitorsList
    ?.filter((item: CompetitorType) => !item?.self)
    ?.map((item: CompetitorType) => ({
      label: item.name,
      value: item.name,
    }))

  const callBack = () => {
    setSaveLoading(false)
  }

  const handleSubmit = async () => {
    await formikProps.validateField(
      `keywordCompetitorMapping[${index}].competitors`
    )
    await formikProps.validateField(
      `keywordCompetitorMapping[${index}].keywords`
    )
    await formikProps.setFieldTouched(
      `keywordCompetitorMapping[${index}].competitors`,
      true
    )
    await formikProps.setFieldTouched(
      `keywordCompetitorMapping[${index}].keywords`,
      true
    )

    const isErrors =
      get(
        formikProps.errors,
        `keywordCompetitorMapping.${[index]}.keywords`,
        ''
      ) ||
      get(
        formikProps.errors,
        `keywordCompetitorMapping.${[index]}.competitors`,
        ''
      )

    if (!isErrors && accountId) {
      setSaveLoading(true)
      await dispatch(
        addEditKeywordSection({
          ...(keywordCompetitorList?.length > index
            ? { existingKeywordsCompetitors: keywordCompetitorList[index] }
            : {}),
          updatedKeywordCompetitorMapping:
            formikProps.values.keywordCompetitorMapping[index],
          cloudMarketplace: selectedCloud,
          partnerId: accountId,
          callBack: callBack,
        })
      )
    }
  }

  const getIsError = (path: string) => {
    return !!(get(touched, path, false) && get(errors, path, false))
  }
  const getErrorText = (path: string) => {
    return (!!get(touched, path, false) && get(errors, path, '')) || ''
  }

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value)
    },
    []
  )

  const handleKeywordInputChangeSelect = useCallback(
    async (event: React.ChangeEvent<object>, newValues: string[]) => {
      const updatedValues = [...new Set(newValues)]
      const keywordData = [...existingKeywords, ...newKeywords]?.filter(item =>
        updatedValues.includes(item?.name)
      )
      await setFieldValue(
        `keywordCompetitorMapping.[${index}].keywords`,
        keywordData
      )
    },
    [existingKeywords, newKeywords]
  )

  const handleCopetitorInputChangeSelect = useCallback(
    async (event: React.ChangeEvent<object>, newValues: string[]) => {
      if (newValues.length > 5) {
        return
      }
      const competitorsData = competitorsList?.filter((item: CompetitorType) =>
        newValues.includes(item?.name)
      )
      await setFieldValue(
        `keywordCompetitorMapping.[${index}].competitors`,
        competitorsData
      )
    },
    [competitorsList]
  )

  const finalValue = (keywords || [])?.map(item => item.name)
  const otherSelectedKeyword = values?.keywordCompetitorMapping
    ?.filter((keyword, keywordIndex) => keywordIndex !== index)
    .map(item => item.keywords.map(keywordNameItem => keywordNameItem?.name))
    .flat()
  const filteredOptionList = [
    ...keywordsListOptions,
    ...newKeywordsOptionListOptions,
  ].filter(item => !otherSelectedKeyword.includes(item.value))

  const handleKeyDown = useCallback(
    async (event: any) => {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault()
        const trimmedValue = inputValue.trim()
        const shouldUnique = ![...existingKeywords, ...newKeywords].filter(
          item => item.name === trimmedValue
        )?.length
        if (trimmedValue) {
          const shouldNotUsed = !otherSelectedKeyword?.includes(trimmedValue)
          if (shouldNotUsed) {
            await setFieldValue(
              `keywordCompetitorMapping.[${index}].keywords`,
              [...keywords, { name: trimmedValue, cloud: selectedCloud }]
            )
          }

          if (shouldUnique && shouldNotUsed) {
            await setNewKeywords([
              ...newKeywords,
              { name: trimmedValue, cloud: selectedCloud },
            ])
          }

          await setInputValue('')
        } else if (!shouldUnique) {
          await setInputValue('')
        }
      }
    },
    [keywords, inputValue, newKeywords, existingKeywords]
  )

  const isSaveDisable = getIsSaveDisabled(
    index,
    keywordCompetitorList,
    values.keywordCompetitorMapping[index]
  )

  const handleClose = () => {
    setOpenRemoveModal(false)
  }

  return (
    <div
      className={classes.row}
      key={`keword-section-${index}`}
      data-testid={'keyword-section'}
    >
      <div className={classes.rowItem}>
        <div className={classes.keywordItem}>
          <AutoCompleteWithMultiSelect
            onChange={handleKeywordInputChangeSelect}
            onInputChange={handleInputChange}
            onKeyDown={handleKeyDown}
            id={`keywordCompetitorMapping.${[index]}.keywords`}
            label={'Choose listing keywords to track'}
            options={filteredOptionList}
            value={finalValue}
            inputValue={inputValue}
            error={getIsError(`keywordCompetitorMapping.${[index]}.keywords`)}
            helperText={getErrorText(
              `keywordCompetitorMapping.${[index]}.keywords`
            )}
            onBlur={handleBlur}
          />
        </div>
        <div className={classes.copetitorItem}>
          <LabraToolTipV2
            tooltipPlacement="top"
            description={
              competitors.length >= 5 ? 'Maximum 5 competitors are allowed' : ''
            }
            disabled={competitors.length < 5}
          >
            <div>
              <AutoCompleteWithMultiSelect
                onChange={handleCopetitorInputChangeSelect}
                id={`keywordCompetitorMapping.${[index]}.competitors`}
                label={'Choose maximum 5 competitors to track'}
                options={competitorsListOptions}
                value={(competitors || []).map(item => item?.name)}
                error={getIsError(
                  `keywordCompetitorMapping.${[index]}.competitors`
                )}
                helperText={
                  getErrorText(
                    `keywordCompetitorMapping.${[index]}.competitors`
                  ) ||
                  (competitors.length >= 5
                    ? 'Maximum 5 competitors allowed'
                    : '')
                }
                onBlur={handleBlur}
              />
            </div>
          </LabraToolTipV2>
        </div>
        <div className={classes.rowRightItem}>
          {values?.keywordCompetitorMapping?.length > 1 && (
            <ButtonV2
              data-testid="trash-ranking"
              smallVariant
              styleType="outlined"
              onClick={async () => setOpenRemoveModal(true)}
            >
              Remove
            </ButtonV2>
          )}
          <ButtonV2
            data-testid="trash-contractDimensions"
            smallVariant
            styleType="outlined"
            disabled={!!isSaveDisable}
            onClick={handleSubmit}
            loading={saveLoading}
          >
            Save
          </ButtonV2>
        </div>
      </div>
      <ModalConfirmV2
        title={'Remove keyword tracking?'}
        description={'Are you sure you want to remove this keyword tracking?'}
        open={openRemoveModal}
        titleAcceptButton={'Remove'}
        onAccept={() => {
          handleDelete(index)
          setOpenRemoveModal(false)
        }}
        onDecline={handleClose}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faDeleteRight}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />
    </div>
  )
}
